import * as THREE from 'three';
import {
  ArenaState, PlayerState, BuffType, ClassType,
  ARENA_SIZE, ARENA_PILLARS,
} from '@pvp/shared';

const CLASS_COLORS: Record<ClassType, number> = {
  [ClassType.Warrior]: 0xc79c6e,
  [ClassType.Mage]: 0x69ccf0,
  [ClassType.Priest]: 0xffffff,
  [ClassType.Paladin]: 0xf8c347,
};

const TEAM_COLORS = [0x4488ff, 0xff4444];

interface CharacterMesh {
  group: THREE.Group;
  hitbox: THREE.Mesh;
  casterForm: THREE.Group;
  polymorphForm: THREE.Group;
  nameSprite: THREE.Sprite;
  hpBarBg: THREE.Mesh;
  hpBarFill: THREE.Mesh;
  lastHp: number;
}

export class Renderer {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private characters: Map<string, CharacterMesh> = new Map();
  private cameraOffset = new THREE.Vector3(0, 12, 14);
  private cameraTarget = new THREE.Vector3();
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();

  constructor(private canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x1a1a2e);
    this.scene.fog = new THREE.Fog(0x1a1a2e, 50, 100);

    this.camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 200);
    this.camera.position.set(0, 15, 20);
    this.camera.lookAt(0, 0, 0);

    this.setupLighting();
    this.createArena();

    window.addEventListener('resize', () => this.onResize());
  }

  private createPatternTexture(
    size: number,
    repeatX: number,
    repeatY: number,
    painter: (ctx: CanvasRenderingContext2D, size: number) => void,
  ): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    painter(ctx, size);

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(repeatX, repeatY);
    texture.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());
    return texture;
  }

  private createCobblestoneTexture(repeat = 8): THREE.CanvasTexture {
    return this.createPatternTexture(512, repeat, repeat, (ctx, size) => {
      ctx.fillStyle = '#3f433f';
      ctx.fillRect(0, 0, size, size);

      const cell = 64;
      for (let y = 0; y < size; y += cell) {
        for (let x = 0; x < size; x += cell) {
          const inset = 4 + ((x + y) / cell) % 3;
          const blockW = cell - inset * 2;
          const blockH = cell - inset * 2;
          const shade = 82 + ((x * 13 + y * 7) % 40);
          ctx.fillStyle = `rgb(${shade}, ${shade - 6}, ${shade - 12})`;
          ctx.fillRect(x + inset, y + inset, blockW, blockH);

          ctx.strokeStyle = 'rgba(22, 18, 12, 0.45)';
          ctx.lineWidth = 3;
          ctx.strokeRect(x + inset, y + inset, blockW, blockH);

          for (let i = 0; i < 26; i += 1) {
            const px = x + inset + Math.random() * blockW;
            const py = y + inset + Math.random() * blockH;
            const grain = 70 + Math.random() * 40;
            ctx.fillStyle = `rgba(${grain}, ${grain}, ${grain}, 0.2)`;
            ctx.fillRect(px, py, 2, 2);
          }
        }
      }
    });
  }

  private createStoneBlockTexture(repeatX: number, repeatY: number): THREE.CanvasTexture {
    return this.createPatternTexture(512, repeatX, repeatY, (ctx, size) => {
      ctx.fillStyle = '#4d5258';
      ctx.fillRect(0, 0, size, size);

      const rowHeight = 96;
      for (let y = 0; y < size; y += rowHeight) {
        const offset = (Math.floor(y / rowHeight) % 2) * 36;
        for (let x = -offset; x < size; x += 128) {
          const width = 116;
          const height = rowHeight - 8;
          const shade = 88 + ((x + y) % 28);
          ctx.fillStyle = `rgb(${shade}, ${shade - 5}, ${shade - 10})`;
          ctx.fillRect(x + 6, y + 6, width, height);
          ctx.strokeStyle = 'rgba(24, 20, 18, 0.5)';
          ctx.lineWidth = 4;
          ctx.strokeRect(x + 6, y + 6, width, height);
        }
      }

      for (let i = 0; i < 900; i += 1) {
        const px = Math.random() * size;
        const py = Math.random() * size;
        const grain = 90 + Math.random() * 40;
        ctx.fillStyle = `rgba(${grain}, ${grain}, ${grain}, 0.14)`;
        ctx.fillRect(px, py, 2, 2);
      }
    });
  }

  private createWoodPlankTexture(repeatX: number, repeatY: number): THREE.CanvasTexture {
    return this.createPatternTexture(512, repeatX, repeatY, (ctx, size) => {
      ctx.fillStyle = '#5d3a22';
      ctx.fillRect(0, 0, size, size);

      const plankWidth = 72;
      for (let x = 0; x < size; x += plankWidth) {
        const tone = 96 + ((x / plankWidth) % 3) * 16;
        ctx.fillStyle = `rgb(${tone}, ${tone - 28}, ${tone - 48})`;
        ctx.fillRect(x, 0, plankWidth - 4, size);
        ctx.fillStyle = 'rgba(30, 16, 10, 0.25)';
        ctx.fillRect(x + plankWidth - 6, 0, 4, size);

        ctx.strokeStyle = 'rgba(244, 207, 142, 0.08)';
        for (let y = 0; y < size; y += 18) {
          ctx.beginPath();
          ctx.moveTo(x + 8, y + Math.sin((x + y) * 0.08) * 2);
          ctx.lineTo(x + plankWidth - 12, y + Math.cos((x + y) * 0.06) * 2);
          ctx.stroke();
        }
      }
    });
  }

  private createClothTexture(base: string, stripe: string, repeatX: number, repeatY: number): THREE.CanvasTexture {
    return this.createPatternTexture(256, repeatX, repeatY, (ctx, size) => {
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, size, size);

      ctx.fillStyle = stripe;
      ctx.fillRect(0, 0, size, 20);
      ctx.fillRect(0, size - 20, size, 20);
      ctx.fillRect(size / 2 - 14, 0, 28, size);

      for (let i = 0; i < 500; i += 1) {
        const px = Math.random() * size;
        const py = Math.random() * size;
        ctx.fillStyle = `rgba(255,255,255,${0.03 + Math.random() * 0.06})`;
        ctx.fillRect(px, py, 1, 1);
      }
    });
  }

  private setupLighting(): void {
    const ambient = new THREE.AmbientLight(0x404060, 0.6);
    this.scene.add(ambient);

    const sun = new THREE.DirectionalLight(0xffeedd, 1.2);
    sun.position.set(20, 30, 10);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 80;
    sun.shadow.camera.left = -50;
    sun.shadow.camera.right = 50;
    sun.shadow.camera.top = 50;
    sun.shadow.camera.bottom = -50;
    this.scene.add(sun);

    // Point lights for atmosphere
    const p1 = new THREE.PointLight(0xff6b35, 0.5, 40);
    p1.position.set(-20, 5, -20);
    this.scene.add(p1);

    const p2 = new THREE.PointLight(0x4488ff, 0.5, 40);
    p2.position.set(20, 5, 20);
    this.scene.add(p2);
  }

  private createArena(): void {
    const cobbleTexture = this.createCobblestoneTexture(7);
    const wallTexture = this.createStoneBlockTexture(6, 2);
    const pillarTexture = this.createStoneBlockTexture(2, 2);
    const woodTexture = this.createWoodPlankTexture(3, 1);

    // Ground
    const groundGeo = new THREE.PlaneGeometry(ARENA_SIZE * 2, ARENA_SIZE * 2);
    const groundMat = new THREE.MeshStandardMaterial({
      map: cobbleTexture,
      color: 0xb9a88e,
      roughness: 0.94,
      metalness: 0.04,
    });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    this.scene.add(ground);

    // Grid lines on ground
    const gridHelper = new THREE.GridHelper(ARENA_SIZE * 2, 20, 0x453d31, 0x2d241c);
    gridHelper.position.y = 0.01;
    const gridMaterial = gridHelper.material as THREE.Material;
    gridMaterial.transparent = true;
    gridMaterial.opacity = 0.22;
    this.scene.add(gridHelper);

    // Walls
    const wallMat = new THREE.MeshStandardMaterial({
      map: wallTexture,
      color: 0xc7c0b3,
      roughness: 0.88,
      metalness: 0.05,
    });
    const wallHeight = 5;
    const walls = [
      { pos: [0, wallHeight / 2, -ARENA_SIZE], size: [ARENA_SIZE * 2, wallHeight, 0.5] },
      { pos: [0, wallHeight / 2, ARENA_SIZE], size: [ARENA_SIZE * 2, wallHeight, 0.5] },
      { pos: [-ARENA_SIZE, wallHeight / 2, 0], size: [0.5, wallHeight, ARENA_SIZE * 2] },
      { pos: [ARENA_SIZE, wallHeight / 2, 0], size: [0.5, wallHeight, ARENA_SIZE * 2] },
    ];
    for (const w of walls) {
      const geo = new THREE.BoxGeometry(w.size[0], w.size[1], w.size[2]);
      const mesh = new THREE.Mesh(geo, wallMat);
      mesh.position.set(w.pos[0], w.pos[1], w.pos[2]);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.scene.add(mesh);
    }

    const timberMat = new THREE.MeshStandardMaterial({
      map: woodTexture,
      color: 0xc29a6a,
      roughness: 0.9,
      metalness: 0.04,
    });
    const beams = [
      { pos: [0, wallHeight + 0.3, -ARENA_SIZE], size: [ARENA_SIZE * 2, 0.6, 0.6] },
      { pos: [0, wallHeight + 0.3, ARENA_SIZE], size: [ARENA_SIZE * 2, 0.6, 0.6] },
      { pos: [-ARENA_SIZE, wallHeight + 0.3, 0], size: [0.6, 0.6, ARENA_SIZE * 2] },
      { pos: [ARENA_SIZE, wallHeight + 0.3, 0], size: [0.6, 0.6, ARENA_SIZE * 2] },
    ];
    for (const beam of beams) {
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(beam.size[0], beam.size[1], beam.size[2]), timberMat);
      mesh.position.set(beam.pos[0], beam.pos[1], beam.pos[2]);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.scene.add(mesh);
    }

    // Pillars
    for (const pillar of ARENA_PILLARS) {
      const geo = new THREE.CylinderGeometry(pillar.radius, pillar.radius, pillar.height, 16);
      const mat = new THREE.MeshStandardMaterial({
        map: pillarTexture,
        color: 0xd0cbc0,
        roughness: 0.86,
        metalness: 0.06,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(pillar.position.x, pillar.height / 2, pillar.position.z);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.scene.add(mesh);
    }

    const bannerBlue = this.createClothTexture('#34527f', '#c8a856', 1, 1);
    const bannerRed = this.createClothTexture('#6f2d2d', '#d3b164', 1, 1);
    const bannerGeo = new THREE.PlaneGeometry(8, 4);
    const banners = [
      { texture: bannerBlue, pos: [-16, 3.6, -ARENA_SIZE + 0.4], rotY: 0 },
      { texture: bannerRed, pos: [16, 3.6, ARENA_SIZE - 0.4], rotY: Math.PI },
      { texture: bannerBlue, pos: [-ARENA_SIZE + 0.4, 3.6, 16], rotY: Math.PI / 2 },
      { texture: bannerRed, pos: [ARENA_SIZE - 0.4, 3.6, -16], rotY: -Math.PI / 2 },
    ];
    for (const banner of banners) {
      const material = new THREE.MeshStandardMaterial({
        map: banner.texture,
        color: 0xffffff,
        side: THREE.DoubleSide,
        roughness: 0.88,
      });
      const mesh = new THREE.Mesh(bannerGeo, material);
      mesh.position.set(banner.pos[0], banner.pos[1], banner.pos[2]);
      mesh.rotation.y = banner.rotY;
      this.scene.add(mesh);
    }
  }

  private createCharacter(player: PlayerState): CharacterMesh {
    const group = new THREE.Group();
    const casterForm = new THREE.Group();
    const polymorphForm = new THREE.Group();
    polymorphForm.visible = false;

    const clothPrimary = player.classType === ClassType.Warrior ? '#6b4f3a'
      : player.classType === ClassType.Mage ? '#395287'
      : player.classType === ClassType.Paladin ? '#7a5c00'
      : '#c9c6bf';
    const clothSecondary = player.classType === ClassType.Warrior ? '#b38a51'
      : player.classType === ClassType.Mage ? '#8fc7ff'
      : player.classType === ClassType.Paladin ? '#f8c347'
      : '#d9b86a';
    const clothTexture = this.createClothTexture(clothPrimary, clothSecondary, 1, 2);
    const leatherTexture = this.createWoodPlankTexture(1, 2);

    const hitbox = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.75, 1.2, 4, 8),
      new THREE.MeshBasicMaterial({ transparent: true, opacity: 0 }),
    );
    hitbox.position.y = 1.1;
    group.add(hitbox);

    // Body - capsule shape
    const bodyGeo = new THREE.CapsuleGeometry(0.4, 1, 8, 16);
    const bodyMat = new THREE.MeshStandardMaterial({
      color: CLASS_COLORS[player.classType],
      map: clothTexture,
      roughness: 0.82,
      metalness: 0.08,
    });
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    body.position.y = 1.1;
    body.castShadow = true;
    casterForm.add(body);

    // Team indicator (ring at feet)
    const ringGeo = new THREE.RingGeometry(0.5, 0.7, 32);
    const ringMat = new THREE.MeshBasicMaterial({
      color: TEAM_COLORS[player.team] || 0xffffff,
      side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.02;
    group.add(ring);

    // Weapon
    let weapon: THREE.Mesh;
    if (player.classType === ClassType.Warrior) {
      const swordGeo = new THREE.BoxGeometry(0.08, 1.2, 0.15);
      weapon = new THREE.Mesh(swordGeo, new THREE.MeshStandardMaterial({
        map: leatherTexture,
        color: 0xb9b7c1,
        roughness: 0.28,
        metalness: 0.82,
      }));
      weapon.position.set(0.6, 1.2, 0);
      weapon.rotation.z = -0.3;
    } else if (player.classType === ClassType.Mage) {
      const staffGeo = new THREE.CylinderGeometry(0.04, 0.04, 1.8, 8);
      weapon = new THREE.Mesh(staffGeo, new THREE.MeshStandardMaterial({
        map: leatherTexture,
        color: 0x8d6f4a,
        roughness: 0.88,
      }));
      weapon.position.set(0.5, 1.4, 0);
      weapon.rotation.z = -0.2;
      // Orb on top
      const orbGeo = new THREE.SphereGeometry(0.12, 8, 8);
      const orb = new THREE.Mesh(orbGeo, new THREE.MeshStandardMaterial({ color: 0x69ccf0, emissive: 0x69ccf0, emissiveIntensity: 0.5 }));
      orb.position.y = 0.9;
      weapon.add(orb);
    } else if (player.classType === ClassType.Paladin) {
      // Mace: handle + head
      const handleGeo = new THREE.CylinderGeometry(0.04, 0.04, 1.0, 8);
      weapon = new THREE.Mesh(handleGeo, new THREE.MeshStandardMaterial({
        map: leatherTexture,
        color: 0x8d6f4a,
        roughness: 0.72,
      }));
      weapon.position.set(0.6, 1.2, 0);
      weapon.rotation.z = -0.3;
      const headGeo = new THREE.BoxGeometry(0.22, 0.22, 0.22);
      const head = new THREE.Mesh(headGeo, new THREE.MeshStandardMaterial({
        color: 0xd4af37,
        roughness: 0.25,
        metalness: 0.9,
      }));
      head.position.y = 0.55;
      weapon.add(head);
    } else {
      const bookGeo = new THREE.BoxGeometry(0.3, 0.4, 0.08);
      weapon = new THREE.Mesh(bookGeo, new THREE.MeshStandardMaterial({
        map: leatherTexture,
        color: 0xc89b4a,
        roughness: 0.72,
        metalness: 0.1,
      }));
      weapon.position.set(0.5, 1, 0);
    }
    weapon.castShadow = true;
    casterForm.add(weapon);

    const sheepBody = new THREE.Mesh(
      new THREE.SphereGeometry(0.6, 18, 18),
      new THREE.MeshStandardMaterial({ color: 0xf2eee3, roughness: 0.95 }),
    );
    sheepBody.scale.set(1.25, 1, 1.45);
    sheepBody.position.set(0, 0.85, 0);
    sheepBody.castShadow = true;
    polymorphForm.add(sheepBody);

    const sheepHead = new THREE.Mesh(
      new THREE.SphereGeometry(0.3, 16, 16),
      new THREE.MeshStandardMaterial({ color: 0x2f2c31, roughness: 0.9 }),
    );
    sheepHead.position.set(0, 0.92, 0.72);
    sheepHead.castShadow = true;
    polymorphForm.add(sheepHead);

    const earGeo = new THREE.BoxGeometry(0.1, 0.05, 0.22);
    const earMaterial = new THREE.MeshStandardMaterial({ color: 0x2f2c31, roughness: 0.9 });
    const leftEar = new THREE.Mesh(earGeo, earMaterial);
    leftEar.position.set(-0.18, 1.08, 0.82);
    leftEar.rotation.z = 0.4;
    polymorphForm.add(leftEar);

    const rightEar = new THREE.Mesh(earGeo, earMaterial);
    rightEar.position.set(0.18, 1.08, 0.82);
    rightEar.rotation.z = -0.4;
    polymorphForm.add(rightEar);

    const legGeo = new THREE.CylinderGeometry(0.08, 0.09, 0.65, 8);
    const legMaterial = new THREE.MeshStandardMaterial({ color: 0x2c2a2b, roughness: 0.9 });
    for (const [x, z] of [[-0.28, -0.22], [0.28, -0.22], [-0.28, 0.24], [0.28, 0.24]] as Array<[number, number]>) {
      const leg = new THREE.Mesh(legGeo, legMaterial);
      leg.position.set(x, 0.33, z);
      polymorphForm.add(leg);
    }

    // Name sprite
    const nameCanvas = document.createElement('canvas');
    nameCanvas.width = 256;
    nameCanvas.height = 64;
    const ctx = nameCanvas.getContext('2d')!;
    ctx.font = 'bold 28px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillStyle = player.team === 0 ? '#4488ff' : '#ff4444';
    ctx.fillText(player.name, 128, 40);
    const nameTexture = new THREE.CanvasTexture(nameCanvas);
    const nameMat = new THREE.SpriteMaterial({ map: nameTexture, transparent: true });
    const nameSprite = new THREE.Sprite(nameMat);
    nameSprite.position.y = 2.8;
    nameSprite.scale.set(2, 0.5, 1);
    group.add(nameSprite);

    // HP bar background
    const hpBgGeo = new THREE.PlaneGeometry(1.2, 0.12);
    const hpBgMat = new THREE.MeshBasicMaterial({ color: 0x333333, side: THREE.DoubleSide });
    const hpBarBg = new THREE.Mesh(hpBgGeo, hpBgMat);
    hpBarBg.position.y = 2.4;
    group.add(hpBarBg);

    // HP bar fill
    const hpFillGeo = new THREE.PlaneGeometry(1.2, 0.12);
    const hpFillMat = new THREE.MeshBasicMaterial({ color: 0x2ecc40, side: THREE.DoubleSide });
    const hpBarFill = new THREE.Mesh(hpFillGeo, hpFillMat);
    hpBarFill.position.y = 2.4;
    hpBarFill.position.z = 0.001;
    group.add(hpBarFill);

    group.add(casterForm);
    group.add(polymorphForm);
    group.position.set(player.position.x, 0, player.position.z);
    this.scene.add(group);

    return { group, hitbox, casterForm, polymorphForm, nameSprite, hpBarBg, hpBarFill, lastHp: player.hp };
  }

  updateState(state: ArenaState, myPlayerId: string, cameraYaw: number): void {
    const existingIds = new Set(Object.keys(state.players));

    // Remove old characters
    for (const [id, char] of this.characters) {
      if (!existingIds.has(id)) {
        this.scene.remove(char.group);
        this.characters.delete(id);
      }
    }

    // Update or create characters
    for (const [id, player] of Object.entries(state.players)) {
      let char = this.characters.get(id);
      if (!char) {
        char = this.createCharacter(player);
        this.characters.set(id, char);
      }

      // Interpolate position
      const targetPos = new THREE.Vector3(player.position.x, 0, player.position.z);
      char.group.position.lerp(targetPos, 0.3);

      // Rotate the full character instead of just the body mesh.
      char.group.rotation.y = player.rotation;

      const polymorphed = player.buffs.some((buff) => buff.type === BuffType.Polymorph);
      char.casterForm.visible = !polymorphed;
      char.polymorphForm.visible = polymorphed;
      if (polymorphed) {
        char.polymorphForm.rotation.y += 0.02;
      }

      // HP bar
      const hpPct = player.hp / player.maxHp;
      char.hpBarFill.scale.x = Math.max(0.001, hpPct);
      char.hpBarFill.position.x = -(1 - hpPct) * 0.6;

      // HP color
      const hpColor = hpPct > 0.5 ? 0x2ecc40 : hpPct > 0.25 ? 0xf39c12 : 0xe74c3c;
      (char.hpBarFill.material as THREE.MeshBasicMaterial).color.setHex(hpColor);

      // Visibility
      char.group.visible = player.alive;

      // Billboard name + hp bar to camera
      char.nameSprite.lookAt(this.camera.position);
      char.hpBarBg.lookAt(this.camera.position);
      char.hpBarFill.lookAt(this.camera.position);

      char.lastHp = player.hp;
    }

    // Camera follow
    const myPlayer = state.players[myPlayerId];
    if (myPlayer) {
      const playerPos = new THREE.Vector3(myPlayer.position.x, 0, myPlayer.position.z);

      // Rotate camera offset by yaw
      const offset = this.cameraOffset.clone();
      const cos = Math.cos(cameraYaw);
      const sin = Math.sin(cameraYaw);
      const rotatedOffset = new THREE.Vector3(
        offset.x * cos - offset.z * sin,
        offset.y,
        offset.x * sin + offset.z * cos,
      );

      const targetCamPos = playerPos.clone().add(rotatedOffset);
      this.camera.position.lerp(targetCamPos, 0.15);
      this.cameraTarget.lerp(playerPos.clone().add(new THREE.Vector3(0, 1.5, 0)), 0.15);
      this.camera.lookAt(this.cameraTarget);
    }
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  private onResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  // Raycasting for click-targeting
  getClickedPlayerId(event: MouseEvent, state: ArenaState, myPlayerId: string): string | null {
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );

    this.raycaster.setFromCamera(this.pointer, this.camera);

    // Check all character bodies
    const meshes: THREE.Mesh[] = [];
    const idMap = new Map<THREE.Mesh, string>();

    for (const [id, char] of this.characters) {
      if (id === myPlayerId) continue;
      meshes.push(char.hitbox);
      idMap.set(char.hitbox, id);
    }

    const intersects = this.raycaster.intersectObjects(meshes);
    if (intersects.length > 0) {
      return idMap.get(intersects[0].object as THREE.Mesh) || null;
    }

    return null;
  }

  getWorldPosition(playerId: string): THREE.Vector3 | null {
    const char = this.characters.get(playerId);
    if (!char) return null;
    return char.group.position.clone();
  }

  projectToScreen(worldPos: THREE.Vector3): { x: number; y: number } | null {
    const projected = worldPos.clone().project(this.camera);
    if (projected.z > 1) return null; // behind camera
    return {
      x: (projected.x * 0.5 + 0.5) * window.innerWidth,
      y: (-projected.y * 0.5 + 0.5) * window.innerHeight,
    };
  }

  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  clearCharacters(): void {
    for (const character of this.characters.values()) {
      this.scene.remove(character.group);
    }
    this.characters.clear();
  }
}
